// Kill Sheet — canonical BOP context resolver (display-only).
//
// Resolves the BOP stack the operator is actually running, for the
// DerivedTable's read-only context section -- so the operator can see and
// verify it without navigating away, same purpose as the Well/Depth and
// Drill Pipe/HWDP/DC provenance context.
//
// Sources, in priority order:
//   1. The Well Profile's own bopRefId (well-specific BOP assignment).
//   2. The active Rig Data Sheet's surfaceBop.bopRefId (rig-level default).
// Either points at a reflibs "bop" category record, which supplies the
// BOP type and bore size (nominalSize). Working pressures come from the
// Rig Data Sheet's own surfaceBop section directly -- those are the
// rig-specific ratings as entered on the Rig Data Sheet, independent of
// which BOP library record (if any) is referenced.
//
// No legacy-profile fallback exists for any of this -- it never existed
// anywhere before the canonical Well/Rig Data Sheet. Unresolvable ->
// null fields; never a fabricated value.
//
// Storage/lookup readers are injectable via `deps` for Node unit tests.

import { getEngineeringSession } from "@/lib/rce/session";
import { getWellProfile } from "@/lib/well/storage";
import { getRigSheet } from "@/lib/rigsheet/storage";
import { findById } from "@/lib/reflibs/lookup";

const _num = (v) => {
  if (v === "" || v == null) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
const _isNonEmpty = (v) => v !== "" && v !== null && v !== undefined;

/**
 * resolveBopContext(deps?) → {
 *   bopType, bopNominalSize, bopWorkingPressure,      // from the reflibs record (or null)
 *   annularWorkingPressure, ramWorkingPressure,        // from the active Rig Data Sheet (or null)
 *   source: "well" | "rig" | null,                     // where bopRefId came from, null if none resolved at all
 * }
 */
export function resolveBopContext(deps = {}) {
  const readSession = deps.getEngineeringSession || getEngineeringSession;
  const readWell    = deps.getWellProfile        || getWellProfile;
  const readRig     = deps.getRigSheet           || getRigSheet;
  const findLib     = deps.findById              || findById;

  let eng = null;
  try { eng = readSession(); } catch { eng = null; }
  const wellId = (eng && eng.wellId) || "";
  const rigId  = (eng && eng.rigId)  || "";

  let bopRefId = "";
  let refSource = null;
  if (wellId) {
    try {
      const w = readWell(wellId);
      if (w && _isNonEmpty(w.bopRefId)) { bopRefId = w.bopRefId; refSource = "well"; }
    } catch { /* fall through */ }
  }

  let rigPayload = null;
  if (rigId) {
    try {
      const rs = readRig(rigId);
      rigPayload = rs ? rs.payload : null;
    } catch { rigPayload = null; }
    if (!bopRefId && _isNonEmpty(rigPayload?.surfaceBop?.bopRefId)) {
      bopRefId = rigPayload.surfaceBop.bopRefId;
      refSource = "rig";
    }
  }

  let lib = null;
  if (bopRefId) {
    try { lib = findLib(bopRefId); } catch { lib = null; }
  }

  const annularWorkingPressure = _num(rigPayload?.surfaceBop?.annularWorkingPressure);
  const ramWorkingPressure     = _num(rigPayload?.surfaceBop?.ramWorkingPressure);

  return {
    bopType:            lib?.bopType || null,
    bopNominalSize:      _isNonEmpty(lib?.nominalSize) ? lib.nominalSize : null,
    bopWorkingPressure:  _num(lib?.workingPressure),
    annularWorkingPressure,
    ramWorkingPressure,
    source: lib ? refSource : null,
  };
}
